import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChevronRight } from 'lucide-react';
import { apiService } from '@/lib/api';

const NAVY = '#13294B';
const BLUE = '#1E4ED8';
const SUB = '#5B6678';
const BORDER = '#E4E9F0';

const fmtFull = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtK = (n) => {
  n = Number(n || 0);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

function Switcher({ active }) {
  const link = (to, label, on) => (
    <a href={to} className="px-3 py-1 rounded-md text-[13px] font-semibold" style={on ? { background: NAVY, color: '#fff' } : { color: SUB }}>{label}</a>
  );
  return (
    <div className="w-full border-b bg-white" style={{ borderColor: BORDER }}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-11 flex items-center gap-2">
        <span className="text-[12px] uppercase tracking-wide text-slate-400 mr-2">Design preview</span>
        {link('/dashboard-option-a', 'Option A · Worklist', active === 'a')}
        {link('/dashboard-option-b', 'Option B · Focus', active === 'b')}
        {link('/dashboard', 'Current', false)}
      </div>
    </div>
  );
}

export default function DashboardOptionB({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focusId, setFocusId] = useState(null);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await apiService.getDashboardOverview(user.id);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin" style={{ color: NAVY }} /></div>;
  }
  const d = data || {};
  const active = (d.projects || []).filter((p) => !p.is_complete);
  const started = active.filter((p) => (p.started_phase_count ?? 0) > 0);
  const defaultFocus = [...started].sort((a, b) => (a.readiness_score ?? 0) - (b.readiness_score ?? 0))[0] || active[0];
  const focus = (focusId && active.find((p) => p.id === focusId)) || defaultFocus;
  const items = focus?.draw_items || [];
  const missing = items.filter((i) => !i.ok);
  const score = Math.round(focus?.readiness_score ?? 0);
  const scoreColor = score >= 85 ? '#1F7A4F' : score >= 60 ? '#B45309' : '#B42318';
  const upcoming = d.upcoming_draws || [];

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC', fontFamily: "'Source Sans 3', sans-serif" }}>
      <Switcher active="b" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 pb-6">
          <h1 className="text-3xl md:text-4xl" style={{ color: NAVY, fontFamily: "'Archivo', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' }}>Command</h1>
          <button onClick={() => navigate('/new-project-vision')} className="text-white text-sm font-semibold px-4 py-2.5 rounded-md" style={{ background: NAVY }}>New project</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Focus — one priority draw, flat, terse */}
          <section className="lg:col-span-8 bg-white border rounded-lg" style={{ borderColor: BORDER }}>
            <div className="px-6 pt-6 pb-4 border-b flex items-start justify-between gap-4" style={{ borderColor: BORDER }}>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#98A2B3' }}>Next draw to get funded</div>
                <div className="text-xl font-semibold mt-1 truncate" style={{ color: NAVY }}>{focus?.name || 'No projects yet'}</div>
              </div>
              {active.length > 1 && (
                <select
                  value={focus?.id || ''}
                  onChange={(e) => setFocusId(e.target.value)}
                  className="text-[13px] border rounded-md px-2 py-1.5 bg-white"
                  style={{ borderColor: BORDER, color: SUB }}
                >
                  {active.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>

            {focus && (
              <div className="px-6 py-5">
                {/* readiness meter — thin bar, not a donut */}
                <div className="flex items-center gap-3">
                  <div className="h-2 flex-1 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full" style={{ width: `${score}%`, background: scoreColor }} />
                  </div>
                  <span className="text-lg font-bold tabular-nums" style={{ color: scoreColor }}>{score}</span>
                  <span className="text-[13px]" style={{ color: SUB }}>{missing.length === 0 ? 'ready' : `${missing.length} to fix`}</span>
                </div>

                <div className="mt-5 divide-y" style={{ borderColor: BORDER }}>
                  {items.length === 0 ? (
                    <p className="text-[14px] py-2" style={{ color: SUB }}>Start work on a line item to build this draw.</p>
                  ) : items.map((it, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: it.ok ? '#1F7A4F' : '#B42318' }} />
                      <span className="text-[14px] flex-1" style={{ color: '#1D2939' }}>{it.label}</span>
                      <span className="text-[13px] font-semibold" style={{ color: it.ok ? '#1F7A4F' : '#B42318' }}>
                        {it.ok ? 'Collected' : (it.count > 1 ? `${it.count} needed` : 'Missing')}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => navigate(`/project/${focus.id}?tab=loan`)} className="text-white text-sm font-semibold px-5 py-2.5 rounded-md" style={{ background: BLUE }}>
                    {missing.length === 0 ? 'Prepare draw' : 'Fix draw items'}
                  </button>
                  <button onClick={() => navigate(`/project/${focus.id}`)} className="text-sm font-semibold px-5 py-2.5 rounded-md border bg-white" style={{ borderColor: BORDER, color: NAVY }}>Open project</button>
                </div>
              </div>
            )}
          </section>

          {/* Right rail — portfolio + next up, plain stat rows */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-white border rounded-lg p-5" style={{ borderColor: BORDER }}>
              <div className="text-[11px] uppercase tracking-wide font-semibold mb-3" style={{ color: '#98A2B3' }}>Portfolio</div>
              {[
                ['Total budget', fmtFull(d.total_budget), '#1D2939'],
                ['Drawn to date', fmtFull(d.total_drawn), '#1D2939'],
                ['Available to draw', fmtFull(d.total_available_for_draw), '#1F7A4F'],
                ['Projected overage', fmtFull(d.projected_overage || 0), '#B42318'],
              ].map(([l, v, c]) => (
                <div key={l} className="flex items-center justify-between py-2 border-t first:border-0" style={{ borderColor: BORDER }}>
                  <span className="text-[14px]" style={{ color: SUB }}>{l}</span>
                  <span className="text-[15px] font-semibold tabular-nums" style={{ color: c }}>{v}</span>
                </div>
              ))}
            </div>

            <div className="bg-white border rounded-lg p-5" style={{ borderColor: BORDER }}>
              <div className="text-[11px] uppercase tracking-wide font-semibold mb-2" style={{ color: '#98A2B3' }}>Next up</div>
              {upcoming.length === 0 ? (
                <p className="text-[14px] py-2" style={{ color: SUB }}>No draws coming up.</p>
              ) : upcoming.slice(0, 5).map((u) => (
                <button key={u.project_id} onClick={() => navigate(`/project/${u.project_id}?tab=budget`)} className="w-full text-left flex items-center justify-between py-2.5 border-t first:border-0 hover:opacity-70" style={{ borderColor: BORDER }}>
                  <span className="text-[14px] truncate pr-2" style={{ color: '#1D2939' }}>{u.project_name}</span>
                  <span className="text-[13px] tabular-nums shrink-0" style={{ color: SUB }}>{u.drawn_pct}% · {fmtK(u.available)}</span>
                </button>
              ))}
            </div>
          </aside>
        </div>

        {/* Projects table below */}
        <section className="mt-8">
          <div className="text-[11px] uppercase tracking-wide font-semibold mb-2" style={{ color: '#98A2B3' }}>All active projects</div>
          <div className="border rounded-lg overflow-hidden bg-white" style={{ borderColor: BORDER }}>
            <table className="w-full">
              <thead>
                <tr className="text-left" style={{ background: '#F8FAFC' }}>
                  {['Project', 'Drawn', 'Available', 'Readiness', 'Next action', ''].map((h, i) => (
                    <th key={h + i} className={`px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold ${i >= 1 && i <= 3 ? 'text-right' : ''}`} style={{ color: '#98A2B3' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.map((p) => {
                  const s = Math.round(p.readiness_score ?? 0);
                  const c = s >= 85 ? '#1F7A4F' : s >= 60 ? '#B45309' : '#B42318';
                  const miss = (p.draw_items || []).filter((i) => !i.ok).length;
                  const avail = Math.max(0, (p.loan_principal_amount || p.total_budget || 0) - (p.amount_drawn || 0));
                  return (
                    <tr key={p.id} onClick={() => navigate(`/project/${p.id}`)} className="border-t cursor-pointer hover:bg-slate-50" style={{ borderColor: BORDER }}>
                      <td className="px-4 py-3 text-[15px] font-semibold truncate max-w-[260px]" style={{ color: NAVY }}>{p.name}</td>
                      <td className="px-4 py-3 text-right text-[14px] tabular-nums" style={{ color: '#1D2939' }}>{p.drawn_pct}%</td>
                      <td className="px-4 py-3 text-right text-[14px] tabular-nums" style={{ color: '#1F7A4F' }}>{fmtK(avail)}</td>
                      <td className="px-4 py-3 text-right text-[14px] font-semibold tabular-nums" style={{ color: c }}>{s}</td>
                      <td className="px-4 py-3 text-[13px]" style={{ color: miss ? '#B42318' : SUB }}>{miss ? `${miss} item${miss > 1 ? 's' : ''} missing` : 'Ready to draw'}</td>
                      <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 inline" style={{ color: '#98A2B3' }} /></td>
                    </tr>
                  );
                })}
                {active.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm" style={{ color: SUB }}>No active projects yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
